import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiGet } from '../api.js';
import { calculatePendingInterest } from '../utils/interestCalculation';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const typeColor = (type) => {
  switch (type) {
    case 'Loan':
    case 'Top-up':
      return 'purple';
    case 'Interest':
      return 'green';
    case 'Principal':
      return 'blue';
    case 'Release':
      return 'orange';
    default:
      return 'gray';
  }
};

/** Build chronological ledger rows from all articles for one customer. */
const buildLedgerRows = (articles) => {
  const rows = [];

  (articles || []).forEach((article) => {
    const articleLabel = article.nameOrNo || 'Article';

    if (article.loans?.length) {
      article.loans.forEach((loan, idx) => {
        rows.push({
          id: `${article._id}-loan-${idx}`,
          date: loan.date,
          type: idx === 0 && (!loan.description || loan.description === 'Initial') ? 'Loan' : (loan.description || 'Top-up'),
          article: articleLabel,
          amount: Number(loan.amount) || 0,
          details: `${loan.interestRate || article.interestRate || '-'}% · ${article.weight || '-'}g`,
          articleId: article._id
        });
      });
    } else if (article.amount) {
      rows.push({
        id: `${article._id}-loan-legacy`,
        date: article.date,
        type: 'Loan',
        article: articleLabel,
        amount: Number(article.amount) || 0,
        details: `${article.interestRate || '-'}% · ${article.weight || '-'}g`,
        articleId: article._id
      });
    }

    (article.payments || []).forEach((payment, idx) => {
      const payType = payment.type === 'interest' ? 'Interest' : 'Principal';
      rows.push({
        id: `${article._id}-pay-${idx}`,
        date: payment.date,
        type: payType,
        article: articleLabel,
        amount: Number(payment.amount) || 0,
        details: payment.description
          || (payment.type === 'interest' && payment.months ? `${payment.months} month(s)` : ''),
        articleId: article._id
      });
    });

    if (article.released && article.releaseDate) {
      rows.push({
        id: `${article._id}-release`,
        date: article.releaseDate,
        type: 'Release',
        article: articleLabel,
        amount: Number(article.releaseAmount) || 0,
        details: 'Article released',
        articleId: article._id
      });
    }
  });

  return rows.sort((a, b) => new Date(a.date) - new Date(b.date));
};

const CustomerLedgerReport = () => {
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewArticleId, setViewArticleId] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await apiGet('/clients');
        setClients(res.clients || []);
      } catch (err) {
        setError(err.message || 'Failed to load clients');
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClients = (clients || []).filter((c) =>
    ((c && c.name) || '').toLowerCase().includes((clientSearch || '').toLowerCase())
  );

  const selectClient = async (client) => {
    setSelectedClient(client);
    setClientSearch(client.name);
    setIsDropdownOpen(false);
    setViewArticleId(null);
    setLoading(true);
    setError('');
    try {
      const res = await apiGet(`/articles?clientId=${client._id}`);
      setArticles(res.articles || []);
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedClient(null);
    setClientSearch('');
    setArticles([]);
    setViewArticleId(null);
  };

  const ledgerRows = useMemo(() => buildLedgerRows(articles), [articles]);

  const activeArticles = articles.filter((a) => !a.released);
  const releasedArticles = articles.filter((a) => a.released);
  const totalActivePrincipal = activeArticles.reduce(
    (sum, a) => sum + (Number(a.totalPrincipal) || Number(a.amount) || 0),
    0
  );
  const totalPendingInterest = activeArticles.reduce(
    (sum, a) => sum + calculatePendingInterest(a, new Date()),
    0
  );
  const totalInterestPaid = articles.reduce(
    (sum, a) => sum + (a.payments || [])
      .filter((p) => p.type === 'interest')
      .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    0
  );

  const viewArticle = articles.find((a) => a._id === viewArticleId) || null;

  return (
    <Stack spacing={5}>
      <Box>
        <Heading size="md" mb={1}>Customer Ledger</Heading>
        <Text fontSize="sm" color="gray.600">
          Search a customer to see all loan, payment, and release transactions.
        </Text>
      </Box>

      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
        <Text fontSize="sm" fontWeight="bold" mb={2}>Search Customer</Text>
        <Flex gap={2}>
          <Box flex={1} position="relative" ref={dropdownRef}>
            <Input
              placeholder="Type customer name..."
              value={clientSearch}
              onChange={(e) => {
                setClientSearch(e.target.value);
                setIsDropdownOpen(true);
                if (!e.target.value) clearSelection();
              }}
              onFocus={() => setIsDropdownOpen(true)}
            />
            {isDropdownOpen && clientSearch && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={10}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="lg"
                maxH="220px"
                overflowY="auto"
                as="ul"
                listStyleType="none"
                m={0}
                p={0}
              >
                {filteredClients.map((client) => (
                  <Box
                    as="li"
                    key={client._id}
                    px={4}
                    py={2}
                    _hover={{ bg: 'blue.50', cursor: 'pointer' }}
                    onClick={() => selectClient(client)}
                    fontWeight={selectedClient?._id === client._id ? 'bold' : 'normal'}
                    bg={selectedClient?._id === client._id ? 'blue.50' : 'white'}
                  >
                    {client.name}
                    {client.contact && (
                      <Text as="span" fontSize="xs" color="gray.500" ml={2}>({client.contact})</Text>
                    )}
                  </Box>
                ))}
                {filteredClients.length === 0 && (
                  <Box as="li" px={4} py={2} color="gray.500" fontSize="sm">
                    No customers found.
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {selectedClient && (
            <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
          )}
        </Flex>
      </Box>

      {error && <Text color="red.500">{error}</Text>}
      {loading && <Text color="gray.500">Loading transactions...</Text>}

      {!selectedClient && !loading && (
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={8} textAlign="center">
          <Text color="gray.500">Search and select a customer to view their full ledger.</Text>
        </Box>
      )}

      {selectedClient && !loading && (
        <>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} gap={3}>
            <Box bg="white" borderWidth="1px" borderRadius="lg" p={3}>
              <Text fontSize="xs" color="gray.500">Customer</Text>
              <Text fontWeight="bold">{selectedClient.name}</Text>
              {selectedClient.contact && (
                <Text fontSize="xs" color="gray.500">{selectedClient.contact}</Text>
              )}
            </Box>
            <Box bg="white" borderWidth="1px" borderRadius="lg" p={3}>
              <Text fontSize="xs" color="gray.500">Articles</Text>
              <Text fontWeight="bold">{articles.length}</Text>
            </Box>
            <Box bg="white" borderWidth="1px" borderRadius="lg" p={3}>
              <Text fontSize="xs" color="gray.500">Active / Released</Text>
              <Text fontWeight="bold">{activeArticles.length} / {releasedArticles.length}</Text>
            </Box>
            <Box bg="white" borderWidth="1px" borderRadius="lg" p={3}>
              <Text fontSize="xs" color="gray.500">Active Principal</Text>
              <Text fontWeight="bold" color="green.600">{fmt(totalActivePrincipal)}</Text>
            </Box>
            <Box bg="white" borderWidth="1px" borderRadius="lg" p={3}>
              <Text fontSize="xs" color="gray.500">Pending Interest</Text>
              <Text fontWeight="bold" color="orange.600">{fmt(totalPendingInterest)}</Text>
            </Box>
            <Box bg="white" borderWidth="1px" borderRadius="lg" p={3}>
              <Text fontSize="xs" color="gray.500">Interest Paid</Text>
              <Text fontWeight="bold">{fmt(totalInterestPaid)}</Text>
            </Box>
          </SimpleGrid>

          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
            <Text fontWeight="bold" mb={3}>Transaction Ledger ({ledgerRows.length})</Text>
            <Box overflowX="auto">
              <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Article</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRows.length > 0 ? ledgerRows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(row.date)}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                        <Badge colorScheme={typeColor(row.type)}>{row.type}</Badge>
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{row.article}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right', fontWeight: 600 }}>
                        {fmt(row.amount)}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{row.details || '-'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td style={{ padding: '8px' }} colSpan={5}>No transactions found for this customer.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>

          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
            <Text fontWeight="bold" mb={3}>Articles</Text>
            <Box overflowX="auto">
              <table style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Name/No</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Principal</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Pending Int.</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }} />
                  </tr>
                </thead>
                <tbody>
                  {articles.length > 0 ? articles.map((article) => {
                    const pending = article.released
                      ? 0
                      : calculatePendingInterest(article, new Date());
                    return (
                      <tr key={article._id}>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.nameOrNo || 'N/A'}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(article.date)}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right', fontWeight: 600 }}>
                          {fmt(article.totalPrincipal ?? article.amount)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                          {fmt(pending)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                          <Badge colorScheme={article.released ? 'green' : 'orange'}>
                            {article.released ? 'Released' : 'Active'}
                          </Badge>
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant={viewArticleId === article._id ? 'solid' : 'ghost'}
                            onClick={() => setViewArticleId(viewArticleId === article._id ? null : article._id)}
                          >
                            {viewArticleId === article._id ? 'Hide' : 'Details'}
                          </Button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td style={{ padding: '8px' }} colSpan={6}>No articles for this customer.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>

          {viewArticle && (
            <Box bg="white" borderWidth="1px" borderColor="blue.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontWeight="bold" fontSize="lg">Details: {viewArticle.nameOrNo}</Text>
                <Button size="xs" variant="ghost" onClick={() => setViewArticleId(null)}>Close</Button>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} mb={4}>
                <Box>
                  <Text fontSize="xs" color="gray.500">Weight</Text>
                  <Text>{viewArticle.weight}g</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Interest Rate</Text>
                  <Text>{viewArticle.interestRate}%</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Interest Paid Upto</Text>
                  <Text>{formatDate(viewArticle.interestPaidUpto)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Address</Text>
                  <Text>{viewArticle.address || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Description</Text>
                  <Text>{viewArticle.description || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Pending Interest</Text>
                  <Text fontWeight="bold" color="orange.600">
                    {fmt(viewArticle.released ? 0 : calculatePendingInterest(viewArticle, new Date()))}
                  </Text>
                </Box>
              </SimpleGrid>

              <Text fontWeight="bold" mb={2}>Loan History</Text>
              <Box overflowX="auto" mb={4}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewArticle.loans || []).map((loan, idx) => (
                      <tr key={`loan-${idx}`}>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(loan.date)}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                          {loan.description || (idx === 0 ? 'Initial' : 'Top-up')}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                          {fmt(loan.amount)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                          {loan.interestRate || viewArticle.interestRate}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {(viewArticle.payments || []).length > 0 && (
                <>
                  <Text fontWeight="bold" mb={2}>Payment History</Text>
                  <Box overflowX="auto" mb={4}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewArticle.payments.map((payment, idx) => (
                          <tr key={`pay-${idx}`}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(payment.date)}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                              <Badge colorScheme={payment.type === 'interest' ? 'green' : 'blue'}>
                                {String(payment.type || '').toUpperCase()}
                              </Badge>
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                              {fmt(payment.amount)}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                              {payment.description || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </>
              )}

              <Flex justify="space-between" align="center" pt={2} borderTop="1px solid #e2e8f0">
                <Text fontWeight="bold" fontSize="lg">Principal Balance</Text>
                <Text fontWeight="bold" fontSize="lg" color="green.600">
                  {fmt(viewArticle.totalPrincipal)}
                </Text>
              </Flex>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default CustomerLedgerReport;
